import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Modal, Button, Badge, DataTable, Column,
  ConfirmDialog, Textarea, Input,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui";
import { Pin, PinOff, Lock, Trash2, RefreshCw, AlertTriangle, Loader2, Power, Download, Plus, Share2, RadioTower, CheckCircle2, XCircle, Undo2 } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { euiccService, EuiccInstalledProfile, EuiccCapacityClient, BackhaulSwitchJob } from "@/services/euiccService";
import { resolveProvisioningMethod } from "@simcheck/shared-types";
import { useDateFormat } from "@/hooks/useDateFormat";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
  modemImei: string;
  modemLabel?: string;
  /** Legacy props — kept for API stability; UI no longer gates on them. */
  isSuperAdmin?: boolean;
  canManagePins?: boolean;
  /**
   * Per-modem eSIM provisioning method (rolled in from the table column).
   * `''` / undefined = "Org default". When `onSaveMethod` is omitted the
   * provisioning-method panel is hidden entirely.
   */
  eSimProvisioningMethod?: string;
  /** Device-level skip flag — drives the effective-method badge. */
  deviceSkipEsimProvisioning?: boolean;
  /** True while a method save is in flight (disables the selector). */
  savingMethod?: boolean;
  /** Persist a new per-modem method. `''` clears it back to Org default. */
  onSaveMethod?: (next: string) => void;
  /**
   * #247: this modem is the device's backhaul (its own uplink). The modal
   * then serves the CACHED inventory (a live card read drops the uplink),
   * replaces Enable with the guided "Switch uplink profile" job, and hides
   * install/delete.
   */
  isBackhaul?: boolean;
  /** Device-level backhaul APN, the default for the switch dialog. */
  backhaulApn?: string | null;
}

const BACKHAUL_PHASE_LABELS: Record<string, string> = {
  accepted: 'Accepted by device',
  preflight: 'Preflight checks',
  download: 'Downloading profile over current uplink',
  enable: 'Enabling profile (uplink down)',
  reattach: 'Re-attaching to network',
  bearer_up: 'Bringing up new uplink',
  verify: 'Verifying new uplink',
  commit: 'Committed',
  drain: 'Notifying SM-DP+',
  rollback_enable: 'Rolling back: re-enabling previous profile',
  rollback_reattach: 'Rolling back: re-attaching',
  rollback_bearer_up: 'Rolling back: restoring uplink',
  rollback_verify: 'Rolling back: verifying',
  restore_prev: 'Restoring previous uplink',
  rolled_back: 'Rolled back',
  completed: 'Completed',
  failed: 'Failed',
  failed_unrecoverable: 'Failed — device may need attention',
  inventory_refresh: 'Reading card',
};

/**
 * Compact relative-time formatter for the Installed / Last used columns.
 * The full ISO timestamp is in the cell's `title` attribute for hover.
 */
function formatRelative(ms: number): string {
  const deltaSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

/** ICCID of the currently-enabled profile in a list, or null if none. */
function enabledIccidOf(profiles?: EuiccInstalledProfile[]): string | null {
  return profiles?.find((p) => p.state === "enabled")?.iccid ?? null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Pending mutation awaiting confirmation. Channeled through the shared
 * <ConfirmDialog> rather than window.confirm or inline panels so we get
 * consistent z-stacking, focus trap, and ESC behavior with the rest of
 * the app.
 */
type PendingAction =
  | { kind: "delete"; iccid: string; profileName?: string | null; autoPinned?: boolean }
  | { kind: "install"; lpaString: string }
  | { kind: "backhaul_switch"; iccid: string; profileName?: string | null }
  | { kind: "pin"; iccid: string; nextPinned: boolean; profileName?: string | null }
  | { kind: "share"; iccid: string; nextShared: boolean; profileName?: string | null; networkName?: string | null; serviceProviderName?: string | null; state?: string }
  | null;

export const EuiccInventoryModal: React.FC<Props> = ({
  isOpen, onClose, deviceId, modemImei, modemLabel,
  eSimProvisioningMethod, deviceSkipEsimProvisioning, savingMethod, onSaveMethod,
  isBackhaul = false, backhaulApn = null,
}) => {
  const { success, error: showError } = useToast();
  const { isSuperAdmin } = useAuth();
  const fmt = useDateFormat();
  // Editable fields for the "Mark shared" confirm dialog (name + optional APN).
  const [shareName, setShareName] = useState("");
  const [shareApn, setShareApn] = useState("");
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState<EuiccInstalledProfile[]>([]);
  const [capacity, setCapacity] = useState<EuiccCapacityClient | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [busyIccid, setBusyIccid] = useState<string | null>(null);
  // True while the background "settle" re-reads are confirming the live
  // enabled state after an open/enable — surfaced as a subtle "Confirming…"
  // hint so the operator knows the badge may still update.
  const [settling, setSettling] = useState(false);

  // Monotonic token. Every refresh/mutation/close bumps it; in-flight settle
  // loops compare against it and bail the moment they're superseded, so a
  // slow re-read can never clobber fresher data (or fire after close).
  const loadGenRef = useRef(0);

  // Install-from-activation-code form
  const [showInstallForm, setShowInstallForm] = useState(false);
  const [installLpa, setInstallLpa] = useState("");
  const [installBusy, setInstallBusy] = useState(false);

  // Pending mutation awaiting shared <ConfirmDialog> resolution
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // #247: backhaul job tracking (switch or inventory refresh).
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [switchApn, setSwitchApn] = useState<string>('');
  const [backhaulJob, setBackhaulJob] = useState<BackhaulSwitchJob | null>(null);
  const [backhaulJobBusy, setBackhaulJobBusy] = useState(false);
  const jobPollRef = useRef<number | null>(null);
  const stopJobPoll = useCallback(() => {
    if (jobPollRef.current !== null) {
      window.clearInterval(jobPollRef.current);
      jobPollRef.current = null;
    }
  }, []);

  /**
   * Right after the modem (re)attaches — on first open and after an enable —
   * the eUICC/lpac can momentarily still report the *previously*-enabled
   * profile, so a single live read shows the wrong "enabled" badge until it
   * settles. Re-read a few times in the background until two consecutive reads
   * agree on which ICCID is enabled (or we hit the cap), then stop. Read-only;
   * mirrors what an operator would otherwise do by clicking Refresh repeatedly.
   */
  const settleEnabledState = useCallback(
    async (gen: number, firstEnabled: string | null) => {
      let prevEnabled = firstEnabled;
      setSettling(true);
      try {
        for (let i = 0; i < 3; i++) {
          await sleep(2500);
          if (loadGenRef.current !== gen) return; // superseded — bail
          let next;
          try {
            next = await euiccService.refreshProfiles(deviceId, modemImei);
          } catch {
            return; // transient on a confirm read — keep what we already show
          }
          if (loadGenRef.current !== gen) return;
          setProfiles(next.installedProfiles || []);
          setCapacity(next.euiccCapacity || null);
          const nextEnabled = enabledIccidOf(next.installedProfiles);
          if (nextEnabled === prevEnabled) return; // stable — done
          prevEnabled = nextEnabled;
        }
      } finally {
        if (loadGenRef.current === gen) setSettling(false);
      }
    },
    [deviceId, modemImei],
  );

  const refresh = useCallback(
    async (settle = false) => {
      const gen = ++loadGenRef.current;
      setLoading(true);
      setRefreshError(null);
      try {
        const data = await euiccService.refreshProfiles(deviceId, modemImei);
        if (loadGenRef.current !== gen) return; // superseded
        setProfiles(data.installedProfiles || []);
        setCapacity(data.euiccCapacity || null);
        setCachedAt(data.cached ? (data.cachedAt || '') : null);
        setLoading(false);
        // The backhaul modem's read is a cached snapshot — nothing to settle,
        // and a live re-read would drop the device's uplink.
        if (settle && !data.cached) await settleEnabledState(gen, enabledIccidOf(data.installedProfiles));
      } catch (err: any) {
        if (loadGenRef.current !== gen) return;
        setRefreshError(err?.message || "Failed to read eUICC");
        showError(err?.message || "Failed to read eUICC");
        setLoading(false);
      }
    },
    [deviceId, modemImei, showError, settleEnabledState],
  );

  useEffect(() => {
    if (isOpen) {
      void refresh(true);
    } else {
      loadGenRef.current++; // cancel any in-flight settle loop
      setProfiles([]);
      setCapacity(null);
      setRefreshError(null);
      setSettling(false);
      setShowInstallForm(false);
      setInstallLpa("");
      setPendingAction(null);
      setConfirmLoading(false);
      setBackhaulJob(null);
      setBackhaulJobBusy(false);
      stopJobPoll();
    }
  }, [isOpen, refresh, stopJobPoll]);

  useEffect(() => () => stopJobPoll(), [stopJobPoll]);

  // #247: poll a running backhaul job until terminal, then refresh the
  // (cached) inventory — the result handler persisted the new card state.
  const trackBackhaulJob = useCallback((jobId: string) => {
    stopJobPoll();
    const terminal = new Set(['completed', 'rolled_back', 'failed', 'lost']);
    const tick = async () => {
      try {
        const job = await euiccService.getBackhaulSwitchJob(jobId);
        setBackhaulJob(job);
        if (terminal.has(job.status)) {
          stopJobPoll();
          setBackhaulJobBusy(false);
          if (job.status === 'completed') success(job.operation === 'backhaul_switch' ? `Uplink switched to ${job.enabledIccid || job.targetIccid}` : 'Card inventory refreshed');
          else if (job.status === 'rolled_back') showError(`Switch rolled back — previous profile restored (${job.code || 'no uplink on target'})`);
          else showError(`Backhaul job ${job.status}: ${job.error || job.code || 'no details'}`);
          void refresh(false);
        }
      } catch (err: any) {
        // Transient — keep polling; the job doc is the source of truth.
        console.warn('[eUICC] backhaul job poll failed:', err?.message || err);
      }
    };
    void tick();
    jobPollRef.current = window.setInterval(tick, 5000);
  }, [refresh, stopJobPoll, success, showError]);

  const startBackhaulJob = async (args: { iccid?: string; mode: 'switch' | 'inventory_refresh' }) => {
    setBackhaulJobBusy(true);
    setBackhaulJob(null);
    try {
      const res = await euiccService.startBackhaulSwitch({
        deviceId,
        iccid: args.iccid,
        apn: args.mode === 'switch' ? (switchApn.trim() || undefined) : undefined,
        mode: args.mode,
      });
      success(args.mode === 'switch'
        ? 'Uplink switch started — the device will go offline for a few minutes while it re-attaches'
        : 'Card read started');
      trackBackhaulJob(res.jobId);
    } catch (err: any) {
      setBackhaulJobBusy(false);
      showError(err?.message || 'Failed to start backhaul job');
    }
  };

  const handleBackhaulSwitchConfirmed = async (iccid: string) => {
    console.log(`[eUICC] backhaul switch CONFIRMED iccid=${iccid} apn=${switchApn}`);
    setConfirmLoading(true);
    try {
      await startBackhaulJob({ iccid, mode: 'switch' });
      setPendingAction(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  // --- Action handlers — invariants:
  //   - Every handler logs the exact iccid it's acting on for a console
  //     paper trail.
  //   - Mutating actions (enable, pin, delete, install) route through the
  //     shared <ConfirmDialog> for explicit confirmation.

  const handlePinConfirmed = async (iccid: string, nextPinned: boolean) => {
    console.log(`[eUICC] pin CONFIRMED iccid=${iccid} pinned=${nextPinned}`);
    setConfirmLoading(true);
    setBusyIccid(iccid);
    try {
      await euiccService.setPin({ deviceId, modemImei, iccid, pinned: nextPinned });
      success(nextPinned ? `Pinned ${iccid}` : `Unpinned ${iccid}`);
      setPendingAction(null);
      await refresh();
    } catch (err: any) {
      showError(err?.message || "Failed to update pin");
    } finally {
      setBusyIccid(null);
      setConfirmLoading(false);
    }
  };

  const handleShareConfirmed = async (
    action: Extract<NonNullable<PendingAction>, { kind: "share" }>,
  ) => {
    console.log(`[eUICC] mark-shared CONFIRMED iccid=${action.iccid} shared=${action.nextShared}`);
    setConfirmLoading(true);
    setBusyIccid(action.iccid);
    try {
      await euiccService.markShared({
        deviceId,
        modemImei,
        iccid: action.iccid,
        shared: action.nextShared,
        name: shareName.trim() || undefined,
        apn: shareApn.trim() || undefined,
        networkName: action.networkName || undefined,
        serviceProviderName: action.serviceProviderName || undefined,
        state: action.state || undefined,
      });
      success(action.nextShared ? `Added ${action.iccid} to shared inventory` : `Removed ${action.iccid} from shared inventory`);
      setPendingAction(null);
      setShareName("");
      setShareApn("");
      await refresh();
    } catch (err: any) {
      showError(err?.message || "Failed to update shared inventory");
    } finally {
      setBusyIccid(null);
      setConfirmLoading(false);
    }
  };

  const handleEnable = async (iccid: string) => {
    console.log(`[eUICC] enable iccid=${iccid}`);
    loadGenRef.current++; // cancel any in-flight open-settle loop
    setBusyIccid(iccid);
    try {
      const result = await euiccService.enableProfile({ deviceId, modemImei, iccid });
      if (result.installedProfiles) setProfiles(result.installedProfiles);
      if (result.euiccCapacity) setCapacity(result.euiccCapacity);
      if (result.success === false) {
        // The edge answers 200 with success:false when the card refused or
        // never answered; the reason carries the ES10c code (#374).
        showError(`Enable failed: ${result.error || 'no reason reported'}`);
        return;
      }
      success(`Enabled ${iccid} — modem re-attaching to network`);
      // The eUICC keeps re-reporting state during the re-attach; settle it.
      setTimeout(() => { void refresh(true); }, 3000);
    } catch (err: any) {
      showError(err?.message || "Failed to enable profile");
    } finally {
      setBusyIccid(null);
    }
  };

  const handleDeleteConfirmed = async (iccid: string) => {
    console.log(`[eUICC] delete CONFIRMED iccid=${iccid}`);
    setConfirmLoading(true);
    setBusyIccid(iccid);
    try {
      const data = await euiccService.deleteProfile({ deviceId, modemImei, iccid });
      if (data.installedProfiles) setProfiles(data.installedProfiles);
      if (data.euiccCapacity) setCapacity(data.euiccCapacity);
      if (data.success === false) {
        showError(`Delete failed: ${data.error || 'no reason reported'}`);
        setPendingAction(null);
        return;
      }
      success(`Deleted profile ${iccid}`);
      setPendingAction(null);
    } catch (err: any) {
      showError(err?.message || "Failed to delete profile");
    } finally {
      setBusyIccid(null);
      setConfirmLoading(false);
    }
  };

  const handleInstallConfirmed = async (lpaString: string) => {
    console.log(`[eUICC] install CONFIRMED lpa=${lpaString.slice(0, 40)}…`);
    setConfirmLoading(true);
    setInstallBusy(true);
    try {
      const result = await euiccService.installProfile({ deviceId, modemImei, lpaString });
      if (!result.success) {
        showError(`Install failed: ${result.error || result.lpacStderr || 'unknown'}`);
      } else {
        success(`Installed profile ${result.iccid || ''} successfully`);
        if (result.installedProfiles) setProfiles(result.installedProfiles);
        if (result.euiccCapacity) setCapacity(result.euiccCapacity);
        setShowInstallForm(false);
        setInstallLpa("");
      }
      setPendingAction(null);
    } catch (err: any) {
      showError(err?.message || "Failed to install profile");
    } finally {
      setInstallBusy(false);
      setConfirmLoading(false);
    }
  };

  // Effective provisioning method — what FlowExecutor does at lock-acquire.
  // Org default is unavailable here (this runs on the admin page, not an
  // org-execution context), so the fallback case is labelled explicitly.
  const currentMethod = eSimProvisioningMethod ?? '';
  const resolvedMethod = resolveProvisioningMethod({
    device: { skipEsimProvisioning: deviceSkipEsimProvisioning },
    modem: { eSimProvisioningMethod: (currentMethod || null) as any },
  });
  const methodBadgeLabel =
    resolvedMethod.method === 'skip' ? 'skip (device override)'
    : resolvedMethod.method === 'sgp32' ? 'SGP.32'
    : resolvedMethod.method === 'lpac_at' ? 'lpac'
    : resolvedMethod.method === 'manual' && resolvedMethod.source === 'fallback' ? 'org default → manual'
    : 'manual';
  const methodBadgeVariant: 'default' | 'secondary' | 'destructive' | 'outline' =
    resolvedMethod.source === 'device_override' ? 'destructive'
    : resolvedMethod.source === 'modem' ? 'default'
    : 'secondary';
  const methodBadgeTooltip =
    resolvedMethod.source === 'device_override'
      ? 'Device-level "Skip eSIM provisioning" is on — overrides everything below.'
      : resolvedMethod.source === 'modem'
        ? `Per-modem setting resolves to '${resolvedMethod.method}'.`
        : 'No per-modem method set. At runtime the executing org\'s default applies; if unset, falls through to "manual" (no auto-provisioning).';

  const freeKb = capacity?.freeNonVolatileMemoryKb;
  const totalKb = capacity?.totalNonVolatileMemoryKb;
  const usagePct =
    typeof freeKb === "number" && typeof totalKb === "number" && totalKb > 0
      ? Math.min(100, Math.round(((totalKb - freeKb) / totalKb) * 100))
      : null;

  const columns: Column<EuiccInstalledProfile>[] = useMemo(() => [
    {
      key: "iccid",
      header: "ICCID",
      width: "16%",
      render: (value: unknown) => (
        <code className="text-xs font-mono whitespace-nowrap">{String(value || "—")}</code>
      ),
    },
    {
      key: "profileName",
      header: "Profile",
      width: "20%",
      render: (value: unknown, row: EuiccInstalledProfile) => {
        const onCard = (value as string) || row.profileNickname || "";
        const display = row.inventoryName || onCard;
        const title = row.inventoryName && onCard && onCard !== row.inventoryName
          ? `On-card name: ${onCard}`
          : display;
        return (
          <div className="text-sm truncate" title={title}>
            {display || "—"}
          </div>
        );
      },
    },
    {
      key: "serviceProviderName",
      header: "Provider",
      width: "11%",
      render: (value: unknown, row: EuiccInstalledProfile) => {
        const onCard = (value as string) || "";
        const display = row.networkName || onCard;
        const title = row.networkName && onCard && onCard !== row.networkName
          ? `On-card SPN: ${onCard}`
          : display;
        return (
          <div className="text-sm text-muted-foreground truncate" title={title}>
            {display || "—"}
          </div>
        );
      },
    },
    {
      key: "state",
      header: "State",
      width: "13%",
      render: (value: unknown, row: EuiccInstalledProfile) => {
        const v = (value as string) || "unknown";
        return (
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={v === "enabled" ? "default" : v === "evicted" ? "destructive" : "secondary"}>
              {v}
            </Badge>
            {row.pinned && (
              <Badge variant="outline" className="gap-1" title="Explicitly pinned by an admin — protected from LRU auto-eviction.">
                <Pin className="w-2.5 h-2.5" />
                pinned
              </Badge>
            )}
            {row.autoPinned && !row.pinned && (
              <Badge variant="outline" className="gap-1 border-amber-500/60 text-amber-700 dark:text-amber-500" title="Auto-pinned: the source activation code is exhausted (downloadCount >= downloadLimit). The LRU refuses to evict because the profile could not be reinstalled. Unpin requires explicit admin action.">
                <Lock className="w-2.5 h-2.5" />
                auto
              </Badge>
            )}
            {row.shared && (
              <Badge variant="outline" className="gap-1 border-blue-500/60 text-blue-700 dark:text-blue-400" title="Part of the super-admin shared eSIM inventory — usable by allowed orgs and protected from deletion.">
                <Share2 className="w-2.5 h-2.5" />
                shared
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "installedAt",
      header: "Installed",
      width: "9%",
      render: (value: unknown) => {
        const ms = typeof value === "number" ? value : null;
        if (!ms) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <span className="text-xs text-muted-foreground whitespace-nowrap" title={fmt.formatDateTime(ms)}>
            {formatRelative(ms)}
          </span>
        );
      },
    },
    {
      key: "lastUsedAt",
      header: "Last used",
      width: "9%",
      render: (value: unknown, row: EuiccInstalledProfile) => {
        // Currently-enabled profiles are "in use right now" — show that
        // rather than a stale timestamp from the previous enable cycle.
        if (row.state === "enabled") {
          return <span className="text-xs text-green-700 dark:text-green-500">now</span>;
        }
        const ms = typeof value === "number" ? value : null;
        if (!ms) return <span className="text-xs text-muted-foreground">never</span>;
        return (
          <span className="text-xs text-muted-foreground whitespace-nowrap" title={fmt.formatDateTime(ms)}>
            {formatRelative(ms)}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      width: "22%",
      render: (_value: unknown, row: EuiccInstalledProfile) => {
        const enabled = row.state === "enabled";
        const evicted = row.state === "evicted";
        const pinned = row.pinned === true;
        const busy = busyIccid === row.iccid;
        if (evicted) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        return (
          <div className="inline-flex items-center gap-1.5 flex-wrap">
            {!enabled && !isBackhaul && (
              <Button
                variant="outline" size="sm" disabled={busy}
                onClick={() => handleEnable(row.iccid)}
                title={`Enable profile ${row.iccid}`}
                className="h-7 px-2"
              >
                <Power className="w-3 h-3 mr-1" />
                <span className="text-xs">Enable</span>
              </Button>
            )}
            {!enabled && isBackhaul && (
              <Button
                variant="outline" size="sm" disabled={busy || backhaulJobBusy}
                onClick={() => {
                  console.log(`[eUICC] backhaul switch REQUEST iccid=${row.iccid}`);
                  setSwitchApn(backhaulApn || '');
                  setPendingAction({ kind: "backhaul_switch", iccid: row.iccid, profileName: row.inventoryName || row.profileName });
                }}
                title="Switch the device uplink to this profile (guided, with automatic rollback)"
                className="h-7 px-2 border-accent-blue/50 text-accent-blue"
              >
                <RadioTower className="w-3 h-3 mr-1" />
                <span className="text-xs">Switch uplink</span>
              </Button>
            )}
            {pinned ? (
              <Button
                variant="outline" size="sm" disabled={busy}
                onClick={() => {
                  console.log(`[eUICC] unpin REQUEST iccid=${row.iccid}`);
                  setPendingAction({ kind: "pin", iccid: row.iccid, nextPinned: false, profileName: row.inventoryName || row.profileName });
                }}
                title={`Unpin ${row.iccid}`}
                className="h-7 px-2"
              >
                <PinOff className="w-3 h-3 mr-1" />
                <span className="text-xs">Unpin</span>
              </Button>
            ) : (
              <Button
                variant="outline" size="sm" disabled={busy}
                onClick={() => {
                  console.log(`[eUICC] pin REQUEST iccid=${row.iccid}`);
                  setPendingAction({ kind: "pin", iccid: row.iccid, nextPinned: true, profileName: row.inventoryName || row.profileName });
                }}
                title={`Pin ${row.iccid}`}
                className="h-7 px-2"
              >
                <Pin className="w-3 h-3 mr-1" />
                <span className="text-xs">Pin</span>
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                variant="outline" size="sm" disabled={busy}
                onClick={() => {
                  const nextShared = !row.shared;
                  console.log(`[eUICC] mark-shared REQUEST iccid=${row.iccid} shared=${nextShared}`);
                  setShareName((row.inventoryName || row.profileName || "").toString());
                  setShareApn("");
                  setPendingAction({
                    kind: "share", iccid: row.iccid, nextShared,
                    profileName: row.inventoryName || row.profileName,
                    networkName: row.networkName || row.serviceProviderName,
                    serviceProviderName: row.serviceProviderName,
                    state: row.state,
                  });
                }}
                title={row.shared ? "Remove from shared inventory" : "Add to super-admin shared inventory"}
                className="h-7 px-2"
              >
                <Share2 className="w-3 h-3 mr-1" />
                <span className="text-xs">{row.shared ? "Unshare" : "Share"}</span>
              </Button>
            )}
            <Button
              variant="outline" size="sm"
              disabled={busy || enabled || pinned || row.shared === true || isBackhaul}
              onClick={() => {
                console.log(`[eUICC] delete REQUEST iccid=${row.iccid} profileName=${row.profileName}`);
                setPendingAction({ kind: "delete", iccid: row.iccid, profileName: row.inventoryName || row.profileName, autoPinned: row.autoPinned === true });
              }}
              title={
                isBackhaul ? "Not available on the device uplink modem"
                : enabled ? "Enable a different profile first"
                : pinned ? "Unpin first"
                : row.shared ? "Unshare first (shared inventory)"
                : `Delete profile ${row.iccid}`
              }
              className="h-7 px-2"
            >
              <Trash2 className="w-3 h-3 mr-1 text-destructive" />
              <span className="text-xs text-destructive">Delete</span>
            </Button>
            {busy && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
          </div>
        );
      },
    },
  ], [busyIccid, fmt, isSuperAdmin, isBackhaul, backhaulApn, backhaulJobBusy]);

  // --- ConfirmDialog wiring ---
  const isConfirmOpen = pendingAction !== null;

  const confirmTitle =
    pendingAction?.kind === "delete" ? "Permanently delete this profile?"
    : pendingAction?.kind === "install" ? "Install this profile on the eUICC?"
    : pendingAction?.kind === "backhaul_switch" ? "Switch the device uplink to this profile?"
    : pendingAction?.kind === "pin" ? (pendingAction.nextPinned ? "Pin this profile?" : "Unpin this profile?")
    : pendingAction?.kind === "share" ? (pendingAction.nextShared ? "Add to shared eSIM inventory?" : "Remove from shared eSIM inventory?")
    : "";

  const confirmDescription: React.ReactNode = !pendingAction ? null
    : pendingAction.kind === "delete" ? (
      <div className="space-y-2">
        <div><strong>ICCID:</strong> <code className="text-xs">{pendingAction.iccid}</code></div>
        {pendingAction.profileName && (
          <div><strong>Profile:</strong> {pendingAction.profileName}</div>
        )}
        {pendingAction.autoPinned ? (
          <p className="text-xs rounded border border-amber-500/50 bg-amber-500/5 p-2">
            <strong>⚠ Auto-pinned profile.</strong> The source activation code is exhausted
            (downloadCount ≥ downloadLimit). Deleting this profile is <strong>permanent</strong> —
            the code cannot reinstall it. The LRU has been refusing to touch this entry
            for exactly this reason. Proceeding requires you to accept the loss.
          </p>
        ) : (
          <p className="text-xs">
            This is <strong>not reversible</strong> unless the activation code has remaining
            downloads — single-use codes that were already consumed CANNOT be reinstalled.
            The eUICC's ISD-P for this profile will be freed (~30 KB).
          </p>
        )}
      </div>
    ) : pendingAction.kind === "install" ? (
      <div className="space-y-2">
        <code className="text-xs font-mono block break-all p-2 bg-muted rounded border border-border">
          {pendingAction.lpaString}
        </code>
        <p className="text-xs">
          Most activation codes are <strong>single-use</strong>. Once consumed the same
          code cannot install the profile again. The modem will be paused for ~30 s
          during install.
        </p>
      </div>
    ) : pendingAction.kind === "backhaul_switch" ? (
      <div className="space-y-3">
        <div><strong>ICCID:</strong> <code className="text-xs">{pendingAction.iccid}</code></div>
        {pendingAction.profileName && (
          <div><strong>Profile:</strong> {pendingAction.profileName}</div>
        )}
        <p className="text-xs">
          This modem is the device's own internet connection. The device will switch the profile,
          re-attach with the APN below and <strong>verify it can reach the console</strong> (up to 5 minutes).
          If the new profile cannot pass traffic, it <strong>automatically rolls back</strong> to the current
          profile. Expect the device to show <strong>offline for ~3–5 minutes</strong>; offline alerts are muted for the window.
        </p>
        <div className="space-y-1">
          <label className="text-xs font-medium">APN for the new uplink</label>
          <Input value={switchApn} onChange={(e) => setSwitchApn(e.target.value)} placeholder="e.g. internet" className="h-8 text-sm font-mono" />
          <p className="text-[11px] text-muted-foreground">
            Leave blank to use the APN recorded on the inventory profile / its group / its network. The switch is refused if no APN can be resolved.
          </p>
        </div>
      </div>
    ) : pendingAction.kind === "pin" ? (
      <div className="space-y-2">
        <div><strong>ICCID:</strong> <code className="text-xs">{pendingAction.iccid}</code></div>
        {pendingAction.profileName && (
          <div><strong>Profile:</strong> {pendingAction.profileName}</div>
        )}
        <p className="text-xs">
          {pendingAction.nextPinned
            ? "Pinned profiles are protected from the LRU auto-evictor — they stay on the eUICC even when capacity pressure forces other profiles to be deleted."
            : "Unpinning lets the LRU auto-evictor consider this profile when freeing capacity for new installs."}
        </p>
      </div>
    ) : pendingAction.kind === "share" ? (
      pendingAction.nextShared ? (
        <div className="space-y-3">
          <div><strong>ICCID:</strong> <code className="text-xs">{pendingAction.iccid}</code></div>
          <p className="text-xs text-muted-foreground">
            Promotes this modem's resident SIM into the super-admin shared inventory.
            Orgs you grant "shared eSIM" access can run tests on it — no download; the
            run lands on this exact modem. It becomes delete-protected until unshared.
          </p>
          <div className="space-y-1">
            <label className="text-xs font-medium">Inventory name</label>
            <Input value={shareName} onChange={(e) => setShareName(e.target.value)} placeholder="e.g. Shared SimCheck — Berlin LTE" className="h-8 text-sm" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Data APN (optional)</label>
            <Input value={shareApn} onChange={(e) => setShareApn(e.target.value)} placeholder="e.g. internet — leave blank to inherit the flow default" className="h-8 text-sm" />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div><strong>ICCID:</strong> <code className="text-xs">{pendingAction.iccid}</code></div>
          <p className="text-xs">
            Removes this SIM from the shared inventory. Allowed orgs will no longer see or
            be able to run tests on it. The physical resident profile is untouched.
          </p>
        </div>
      )
    ) : null;

  const confirmText =
    pendingAction?.kind === "delete" ? "Delete permanently"
    : pendingAction?.kind === "install" ? "Install"
    : pendingAction?.kind === "backhaul_switch" ? "Switch uplink"
    : pendingAction?.kind === "pin" ? (pendingAction.nextPinned ? "Pin" : "Unpin")
    : pendingAction?.kind === "share" ? (pendingAction.nextShared ? "Add to shared" : "Remove")
    : "Confirm";

  const confirmVariant: "destructive" | "warning" | "default" =
    pendingAction?.kind === "delete" ? "destructive"
    : pendingAction?.kind === "install" || pendingAction?.kind === "backhaul_switch" ? "warning"
    : "default";

  const handleConfirm = () => {
    if (!pendingAction) return;
    if (pendingAction.kind === "delete") return handleDeleteConfirmed(pendingAction.iccid);
    if (pendingAction.kind === "install") return handleInstallConfirmed(pendingAction.lpaString);
    if (pendingAction.kind === "backhaul_switch") return handleBackhaulSwitchConfirmed(pendingAction.iccid);
    if (pendingAction.kind === "pin") return handlePinConfirmed(pendingAction.iccid, pendingAction.nextPinned);
    if (pendingAction.kind === "share") return handleShareConfirmed(pendingAction);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`eUICC — ${modemLabel || modemImei}`}
        description={isBackhaul
          ? "This modem is the device uplink. The inventory shown is the last card snapshot — reading the card live would interrupt the device's connection."
          : "Live profile inventory and capacity for this modem's eUICC, read directly via lpac on every refresh."}
        size="xl"
      >
        <div className="space-y-4">
          {isBackhaul && (
            <div className="rounded-md border border-accent-blue/40 bg-accent-blue/5 p-3 text-sm space-y-2">
              <div className="flex items-center gap-2 font-medium">
                <RadioTower className="w-4 h-4 text-accent-blue" />
                Device backhaul modem
                {cachedAt !== null && (
                  <span className="text-xs text-muted-foreground font-normal">
                    — cached inventory{cachedAt ? ` as of ${fmt.formatDateTime(cachedAt)}` : ''}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Enable, install and delete are replaced by <strong>Switch uplink</strong>, which changes the profile,
                re-attaches with the right APN, verifies the device can reach the console and rolls back on failure.
                Reading the card live takes the uplink down for ~1–2 minutes.
              </p>
              {backhaulJob && (
                <div className="rounded border border-border bg-card p-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    {['completed'].includes(backhaulJob.status) ? <CheckCircle2 className="w-3.5 h-3.5 text-status-success" />
                      : backhaulJob.status === 'rolled_back' ? <Undo2 className="w-3.5 h-3.5 text-status-warning" />
                      : ['failed', 'lost'].includes(backhaulJob.status) ? <XCircle className="w-3.5 h-3.5 text-status-error" />
                      : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span className="font-medium">
                      {BACKHAUL_PHASE_LABELS[backhaulJob.phase || ''] || backhaulJob.phase || backhaulJob.status}
                    </span>
                    <span className="text-muted-foreground">({backhaulJob.status})</span>
                  </div>
                  {['dispatched', 'in_progress', 'verifying'].includes(backhaulJob.status) && (
                    <p className="text-[11px] text-muted-foreground">
                      The device is offline while it re-attaches; the result arrives when it reconnects.
                      {backhaulJob.maintenanceUntil ? ` Waiting until ${fmt.formatDateTime(backhaulJob.maintenanceUntil)} at most.` : ''}
                    </p>
                  )}
                  {(backhaulJob.error || backhaulJob.rollbackError) && (
                    <p className="text-[11px] text-status-error">{backhaulJob.error}{backhaulJob.rollbackError ? ` — rollback: ${backhaulJob.rollbackError}` : ''}</p>
                  )}
                  {backhaulJob.phases && backhaulJob.phases.length > 0 && (
                    <ol className="text-[11px] text-muted-foreground space-y-0.5 max-h-28 overflow-y-auto">
                      {backhaulJob.phases.map((p, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <span className="font-mono">{fmt.formatTime(p.at * 1000)}</span>
                          <span>{BACKHAUL_PHASE_LABELS[p.phase] || p.phase}</span>
                          {p.ok === false && <span className="text-status-error">✗</span>}
                          {p.detail && <span className="truncate" title={p.detail}>— {p.detail}</span>}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Provisioning method — rolled in from the Modems table column. */}
          {onSaveMethod && (
            <div className="rounded-md border border-border bg-card p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-medium">Provisioning method</div>
                  <p className="text-xs text-muted-foreground">
                    How a profile is installed when this modem is allocated for a test.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={currentMethod || 'default'}
                    disabled={savingMethod}
                    onValueChange={(v) => onSaveMethod(v === 'default' ? '' : v)}
                  >
                    <SelectTrigger
                      className="h-8 text-xs w-[140px]"
                      title="Per-modem eSIM provisioning method (overridden by device skip flag)"
                    >
                      <SelectValue placeholder="Org default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Org default</SelectItem>
                      <SelectItem value="sgp32">SGP.32</SelectItem>
                      <SelectItem value="lpac_at">lpac (AT)</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                  <Badge
                    variant={methodBadgeVariant}
                    className="whitespace-nowrap"
                    title={methodBadgeTooltip}
                  >
                    → {methodBadgeLabel}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Capacity panel */}
          <div className="rounded-md border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium">eUICC capacity</div>
                {settling && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Re-reading the eUICC until the enabled-profile state settles after the modem (re)attached.">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Confirming live state…
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isBackhaul && (
                  <Button variant="outline" size="sm" onClick={() => setShowInstallForm(!showInstallForm)}>
                    <Plus className="w-3 h-3" />
                    <span className="ml-1">Install profile</span>
                  </Button>
                )}
                {isBackhaul ? (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => startBackhaulJob({ mode: 'inventory_refresh' })}
                    disabled={loading || backhaulJobBusy}
                    title="Read the card live. Interrupts the device uplink for ~1–2 minutes."
                  >
                    {backhaulJobBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    <span className="ml-1">Refresh from card</span>
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => refresh(true)} disabled={loading}>
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    <span className="ml-1">Refresh</span>
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div className="col-span-3">
                <div className="text-muted-foreground text-xs">EID</div>
                <code className="text-xs font-mono break-all">{capacity?.eid || "—"}</code>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Slots used</div>
                <div>{capacity?.installedProfilesCount ?? profiles.length}</div>
              </div>
              <div className="col-span-2">
                <div className="text-muted-foreground text-xs">Free memory</div>
                <div>
                  {typeof freeKb === "number" ? `${freeKb} KB` : "—"}
                  {usagePct !== null && (
                    <span className="ml-2 text-muted-foreground text-xs">({usagePct}% used)</span>
                  )}
                </div>
              </div>
            </div>
            {usagePct !== null && (
              <div className="mt-2 h-2 rounded bg-muted overflow-hidden">
                <div
                  className={
                    usagePct >= 90 ? "h-full bg-destructive"
                    : usagePct >= 70 ? "h-full bg-amber-500"
                    : "h-full bg-green-500"
                  }
                  style={{ width: `${usagePct}%` }}
                />
              </div>
            )}
          </div>

          {/* Install form */}
          {showInstallForm && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
              <div className="text-sm font-medium">Install profile from activation code</div>
              <p className="text-xs text-muted-foreground">
                Paste a full LPA activation string (e.g. <code className="text-[10px]">LPA:1$smdp.example.com$ABC-DEF-123</code>).
              </p>
              <Textarea
                rows={2}
                value={installLpa}
                onChange={(e) => setInstallLpa(e.target.value)}
                placeholder="LPA:1$consumer.rsp.world$ABCD-1234-EFGH-5678"
                disabled={installBusy}
                className="font-mono text-xs"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowInstallForm(false); setInstallLpa(""); }} disabled={installBusy}>
                  Cancel
                </Button>
                <Button
                  variant="default" size="sm"
                  onClick={() => {
                    if (!installLpa.trim()) {
                      showError("Paste an activation code first");
                      return;
                    }
                    setPendingAction({ kind: "install", lpaString: installLpa.trim() });
                  }}
                  disabled={installBusy || !installLpa.trim()}
                >
                  {installBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  <span className="ml-1">{installBusy ? "Installing…" : "Install"}</span>
                </Button>
              </div>
            </div>
          )}

          {/* Profiles table */}
          {refreshError ? (
            <div className="rounded-md border border-destructive bg-destructive/5 p-3 text-sm flex items-start gap-2 text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>{refreshError}</div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={profiles}
              keyExtractor={(row) => row.iccid}
              loading={loading}
              searchable={false}
              paginated={false}
              emptyMessage="No profiles installed on this eUICC."
            />
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => { if (!confirmLoading) setPendingAction(null); }}
        onConfirm={handleConfirm}
        title={confirmTitle}
        description={confirmDescription}
        confirmText={confirmText}
        variant={confirmVariant}
        loading={confirmLoading}
      />
    </>
  );
};
